import { CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useEffect, useMemo, useState } from 'react'
import { LogContent } from '@/components/play/GameLog/LogContent.tsx'
import { useI18n } from '@/lib/i18n'
import { parsePlayerLog } from './utils'
import {
  rebaseSpeechReferences,
  type TextLogSpeechReference,
} from './speech-references'

export function GameLogWithTabs({
  log,
  summaryLog,
  speechReferences,
  scopeKey,
}: {
  log: string
  summaryLog?: string
  speechReferences: readonly TextLogSpeechReference[]
  scopeKey: string
}) {
  const { t } = useI18n()
  const parsedLogs = useMemo(() => {
    const logs = parsePlayerLog(log)
    if (summaryLog) {
      return [...logs, {
        round: -1,
        content: summaryLog,
        sourceStart: -1,
        sourceEnd: -1,
      }]
    }
    return logs
  }, [log, summaryLog])

  const availableRounds = useMemo(() => {
    const rounds = new Set<number>()
    parsedLogs.forEach(p => rounds.add(p.round))
    return Array.from(rounds).sort((a, b) => {
      if (a === -1)
        return 1
      if (b === -1)
        return -1
      return a - b
    })
  }, [parsedLogs])

  const maxRound = useMemo(() => {
    const numericRounds = availableRounds.filter(r => r !== -1)
    return numericRounds.length > 0 ? numericRounds.at(-1)!.toString() : (availableRounds.includes(-1) ? '-1' : '0')
  }, [availableRounds])

  const preferredTab = useMemo(
    () => (availableRounds.includes(-1) ? '-1' : maxRound),
    [availableRounds, maxRound],
  )

  const [selection, setSelection] = useState(() => ({
    scopeKey,
    tab: preferredTab,
  }))

  const isSelectionValid = selection.scopeKey === scopeKey
    && (availableRounds.length === 0
      ? selection.tab === '0'
      : availableRounds.some(round => round.toString() === selection.tab))
  const activeTab = isSelectionValid ? selection.tab : preferredTab

  useEffect(() => {
    if (!isSelectionValid)
      setSelection({ scopeKey, tab: preferredTab })
  }, [isSelectionValid, preferredTab, scopeKey])

  const activeLog = useMemo(() => {
    const round = parseInt(activeTab, 10)
    return parsedLogs.find(p => p.round === round)
  }, [activeTab, parsedLogs])
  const content = activeLog?.content ?? ''
  const activeSpeechReferences = useMemo(() => {
    if (!activeLog || activeLog.sourceStart < 0)
      return []
    return rebaseSpeechReferences(
      speechReferences,
      activeLog.sourceStart,
      activeLog.sourceEnd,
    )
  }, [activeLog, speechReferences])

  return (
    <CardContent className="h-full min-h-0 flex-1 p-0">
      <Tabs
        value={activeTab}
        onValueChange={tab => setSelection({ scopeKey, tab })}
        className="flex h-full flex-col"
      >
        <div className="px-4 pb-2">
          <TabsList className="w-full justify-start px-0">
            {availableRounds.map(r => (
              <TabsTrigger key={r} value={r.toString()} className="!rounded-none">
                {r === -1
                  ? t('play.logTabs.quickView')
                  : (r === 0
                      ? t('play.logTabs.setup')
                      : t('play.logTabs.round', { round: r }))}
              </TabsTrigger>
            ))}
            {availableRounds.length === 0 && (
              <TabsTrigger value="0">{t('play.logTabs.setup')}</TabsTrigger>
            )}
          </TabsList>
        </div>

        <TabsContent value={activeTab} className="mt-0 min-h-0 flex-1 overflow-hidden border-t border-border/50">
          <LogContent
            content={content}
            speechReferences={activeSpeechReferences}
            scopeKey={`${scopeKey}:round:${activeTab}`}
          />
        </TabsContent>
      </Tabs>
    </CardContent>
  )
}
